import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Container, Paper } from "@mui/material";
import { SupervisorAccount } from "@mui/icons-material";
import InfoBoxGroup from "../../base/InfoBoxGroup";
import InfoBox from "../../base/InfoBox";
import Modal from "../../base/Modal";
import { useDispatch, useSelector } from "../../../store";
import AddEditUser, { UserFormSubmitHandler } from "./AddEditUser";
import {
  addUser,
  clearUser,
  editUser,
  fetchUsers,
  selectUser,
} from "../../../slices/user";
import User from "../../../types/user";
import UsersStack from "./UsersStack";
import UsersTable from "./UsersTable";
import { useParams } from "react-router-dom";

interface Props {}

const UsersView: React.FC<Props> = (props) => {
  const { id } = useParams();

  const dispatch = useDispatch();
  const { status, userList, user } = useSelector((store) => store.users);
  const { userCompany } = useSelector((store) => store.auth);

  const formSubmitHandlerRef = useRef<UserFormSubmitHandler | null>(null);

  const [modalOpen, setModalOpen] = useState<"edit" | "none">("none");
  const [userId, setUserId] = useState(false);

  const handleEdit = useCallback(
    (id: number) => {
      let appUser = userList?.find((x) => x.id === +id);
      dispatch(selectUser(appUser));
      setModalOpen("edit");
    },
    [dispatch, userList]
  );

  const handleAdminRoute = (id: number) => {
    window.location.href = `${process.env.REACT_APP_AUTH_URL}/app/users/${id}`;
  };

  const handleClose = () => {
    setModalOpen("none");
    dispatch(clearUser());
    dispatch(fetchUsers());
  };

  const handleSave = async (data: User) => {
    if (modalOpen === "edit") {
      if (!!data.detectorId) {
        await dispatch(editUser(data));
      } else {
        await dispatch(addUser(data));
      }
    }

    handleClose();
  };

  const infoBoxes = [
    {
      text: `Assign a home dashboard to each user in ${
        userCompany?.companyName ?? "your organization"
      } to control system access.`,
      icon: (
        <Box
          component="img"
          height="70px"
          width="100%"
          sx={{ objectFit: "cover" }}
          src={userCompany?.logo || "/static/images/SVPro.png"}
          alt={userCompany?.companyName}
        />
      ),
    },
    {
      text: `Click here to visit the admin portal to update other user properties.`,
      icon: <SupervisorAccount fontSize="inherit" color="warning" />,
      link: `${process.env.REACT_APP_AUTH_URL}/app/users`,
    },
  ];

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    if (!!id && !userId && userList?.length) {
      setUserId(true);
      handleEdit(+id!);
    }
  }, [id, userId, userList, handleEdit]);

  return (
    <Container sx={{ marginBottom: 8 }}>
      <InfoBoxGroup
        header={"Manage Users"}
        content={infoBoxes.map((info, index) => {
          return (
            <InfoBox
              key={index}
              text={info.text}
              icon={info.icon}
              link={info.link}
            />
          );
        })}
      />
      <Paper
        sx={{
          paddingY: 3,
          paddingX: { xs: 2, md: 5 },
          marginBottom: 5,
        }}
      >
        <Box
          display={{ xs: "none", md: "flex" }}
          flexDirection="row"
          justifyContent="center"
          marginY={2}
          sx={{ maxWidth: "100%" }}
        >
          <UsersTable
            handleEdit={handleEdit}
            handleAdminRoute={handleAdminRoute}
          />
        </Box>
        <Box
          display={{ xs: "flex", md: "none" }}
          flexDirection="row"
          justifyContent="center"
          marginY={2}
          sx={{ width: "100%" }}
        >
          <UsersStack
            handleEdit={handleEdit}
            handleAdminRoute={handleAdminRoute}
          />
        </Box>
      </Paper>
      <Modal
        open={modalOpen === "edit"}
        onClose={handleClose}
        onSave={() => formSubmitHandlerRef.current?.()}
        otherFunction={{
          label: "Admin Edit",
          function: () => handleAdminRoute(user?.id!),
        }}
        title={"Edit User"}
        loading={status === "loading"}
      >
        <AddEditUser
          type={"edit"}
          onSubmit={handleSave}
          ref={formSubmitHandlerRef}
        />
      </Modal>
    </Container>
  );
};

export default UsersView;
